import { StrictMode, type ReactNode } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { SupabaseAuthProvider, LoginGate } from './components/auth';
import { VmProvider } from './components/vm';
import { ConditionalDevPanel } from './components/DevPanel';
import { ChatInputProvider } from '@/contexts/ChatInputContext';
import { ImmediateResultProvider } from '@/components/ImmediateResult';
import { TooltipProvider } from '@/components/ui/tooltip';
import { usePageSetup } from '@/hooks/usePageSetup';
import { usePaperTheme, useThemeFlag } from '@/hooks/useThemeFlag';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSessionTitlesStore } from '@/stores/sessionTitles';
import { AGENT_PATH_RE, Titles } from '@/lib/navigation';
import App from './App';
import { Toaster } from 'sonner';
import { AlertCircle, Check, Info, Loader2, TriangleAlert } from 'lucide-react';

const PAPER_TOAST_ICON_CLASS = 'h-4 w-4 shrink-0';
const PAPER_TOAST_ICON_STROKE_WIDTH = 1.6;
const PAPER_TOAST_CLASS =
  'border border-solid border-[var(--hair)] bg-[var(--surface)] text-[var(--fg-2)]';

const PAPER_TOAST_ICONS = {
  success: <Check className={PAPER_TOAST_ICON_CLASS} strokeWidth={PAPER_TOAST_ICON_STROKE_WIDTH} />,
  info: <Info className={PAPER_TOAST_ICON_CLASS} strokeWidth={PAPER_TOAST_ICON_STROKE_WIDTH} />,
  warning: <TriangleAlert className={PAPER_TOAST_ICON_CLASS} strokeWidth={PAPER_TOAST_ICON_STROKE_WIDTH} />,
  error: <AlertCircle className={PAPER_TOAST_ICON_CLASS} strokeWidth={PAPER_TOAST_ICON_STROKE_WIDTH} />,
  loading: <Loader2 className={`${PAPER_TOAST_ICON_CLASS} animate-spin`} strokeWidth={PAPER_TOAST_ICON_STROKE_WIDTH} />,
};

const OptimisticDocumentTitle = () => {
  const { pathname } = useLocation();
  const getCachedTitle = useSessionTitlesStore((s) => s.get);

  const agentMatch = pathname.match(AGENT_PATH_RE);
  const subtitle = agentMatch
    ? Titles.agent(getCachedTitle(agentMatch[1]) ?? 'Chat')
    : pathname.startsWith('/settings/billing')
      ? Titles.billing()
      : pathname.startsWith('/settings')
        ? Titles.settings()
        : pathname.startsWith('/agents')
          ? Titles.agents()
          : pathname.startsWith('/demos')
            ? Titles.demos()
            : Titles.chat();

  useDocumentTitle(subtitle);
  return null;
};

const PresentationRoot = ({ children }: { children: ReactNode }) => {
  usePageSetup();
  useThemeFlag();
  const paperTheme = usePaperTheme();
  const paperToastIcons = paperTheme ? PAPER_TOAST_ICONS : undefined;
  const paperToastOptions = paperTheme
    ? {
        style: {
          background: 'var(--surface)',
          color: 'var(--fg-2)',
          border: '1px solid var(--hair)',
          borderRadius: 'var(--paper-radius-chrome)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
          fontFamily: 'var(--font-sans-paper)',
          fontSize: '13px',
          lineHeight: '1.35',
          gap: '10px',
          minHeight: '40px',
          padding: '10px 12px',
          maxWidth: 'min(360px, calc(100vw - 24px))',
        },
        classNames: {
          toast: PAPER_TOAST_CLASS,
          default: PAPER_TOAST_CLASS,
          success: PAPER_TOAST_CLASS,
          info: PAPER_TOAST_CLASS,
          warning: PAPER_TOAST_CLASS,
          error: PAPER_TOAST_CLASS,
          loading: PAPER_TOAST_CLASS,
          content: 'gap-[2px]',
          title: 'text-[13px] font-medium leading-[1.35] text-[var(--fg-2)]',
          description: 'text-[12px] leading-[1.4] text-[var(--fg-3)]',
          icon: 'text-[var(--fg-3)]',
          actionButton: 'rounded-full bg-[var(--fg)] text-[var(--fg-inverse)] text-[12px] font-medium',
          cancelButton: 'rounded-full bg-transparent text-[var(--fg)] text-[12px]',
          closeButton: 'bg-[var(--surface)] border-[var(--hair)] text-[var(--fg-3)]',
        },
      }
    : undefined;
  return (
    <TooltipProvider delayDuration={200}>
      {children}
      <Toaster
        theme={paperTheme === 'paper-light' ? 'light' : 'dark'}
        position="bottom-right"
        richColors={!paperTheme}
        icons={paperToastIcons}
        style={paperTheme ? { zIndex: 'var(--paper-z-toast)' } : undefined}
        mobileOffset={
          paperTheme
            ? {
                bottom: 'var(--paper-mobile-toast-bottom-offset)',
                left: 'var(--paper-radius-chrome)',
                right: 'var(--paper-radius-chrome)',
              }
            : undefined
        }
        toastOptions={paperToastOptions}
      />
    </TooltipProvider>
  );
};

const Root = () => (
  <StrictMode>
    <BrowserRouter>
      <PresentationRoot>
        <OptimisticDocumentTitle />
        <SupabaseAuthProvider>
          <LoginGate>
            <ChatInputProvider>
              <ImmediateResultProvider>
                <VmProvider>
                  <ConditionalDevPanel />
                  <App />
                </VmProvider>
              </ImmediateResultProvider>
            </ChatInputProvider>
          </LoginGate>
        </SupabaseAuthProvider>
      </PresentationRoot>
    </BrowserRouter>
  </StrictMode>
);

export default Root;
